import React from "react";
import { AbsoluteFill, Easing, interpolate } from "remotion";
import { PASS_DECOR, boardRows, flapChar, wheelAt, wheelTarget } from "./boardingData";
import { Glyph } from "./houseStyle";
import { DB_EVERY, DB_FIRST, DB_HIGHLIGHT, DB_ROWS, DB_TIP, WN_GAPS, WN_HEADER, WN_LINES, WN_OPEN, WN_SPIN, WN_STATUS } from "./introCamera";
import { passportStamps } from "./passportStamps";

/**
 * The two split-flap boards, drawn over the whole frame while the camera holds the trip behind them
 * (`HOLDS_THE_TRIP` in introCamera.ts). What they show is boardingData.ts; this only draws. They carry no
 * header: the board is the title.
 */

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ease = (frame: number, window: number[], easing: (t: number) => number = Easing.inOut(Easing.cubic)) =>
	interpolate(frame, window, [0, 1], { ...clamp, easing });

/** An airport board: near-black panel, warm white flaps, amber headings, a green that glows. */
const BOARD = "#0e1115";
const INK = "#f4f1e8";
const AMBER = "#f6c744";
const GREEN = "#2fe39a";

type BoardProps = {
	frame: number;
	width: number;
	height: number;
	px: (n: number) => number;
	reelData: any[];
	currentTrip: any;
	nightsText: string;
	stopsText: string;
	brandColor: string;
};

/**
 * One split-flap: blank before `spinFrom`, a new character every frame until `settle` with its top half
 * folding down as each flap falls, then its own character, with a small bounce as it lands.
 */
const Flap = ({
	ch,
	settle,
	frame,
	w,
	h,
	seed,
	color = INK,
	spinFrom = -Infinity,
}: {
	ch: string;
	settle: number;
	frame: number;
	w: number;
	h: number;
	seed: number;
	color?: string;
	spinFrom?: number;
}) => {
	const spinning = frame >= spinFrom && frame < settle;
	const shown = frame < spinFrom ? " " : spinning ? flapChar(seed, frame) : ch;
	const fold = spinning ? 0.35 + (0.65 * ((frame * 7 + seed) % 3)) / 2 : 1;
	const bounce = frame === settle ? 0.9 : frame === settle + 1 ? 1.04 : 1;
	const half = (which: "top" | "bottom"): React.CSSProperties => ({
		position: "absolute",
		inset: 0,
		display: "grid",
		placeItems: "center",
		fontFamily: "Poppins",
		fontWeight: 700,
		fontSize: h * 0.72,
		lineHeight: 1,
		color,
		clipPath: which === "top" ? "inset(0 0 50% 0)" : "inset(50% 0 0 0)",
		background: which === "top" ? "linear-gradient(#2b3036, #23282d)" : "linear-gradient(#1f2327, #171a1e)",
	});
	return (
		<div
			style={{
				position: "relative",
				width: w,
				height: h,
				borderRadius: w * 0.1,
				overflow: "hidden",
				boxShadow: "inset 0 -2px 4px rgba(0,0,0,.6)",
				transform: `scaleY(${bounce})`,
			}}
		>
			<div style={half("bottom")}>{shown}</div>
			<div style={{ ...half("top"), transform: `scaleY(${fold})` }}>{shown}</div>
			<div style={{ position: "absolute", left: 0, right: 0, top: "50%", height: Math.max(1, h * 0.025), background: "#07090b" }} />
		</div>
	);
};

/** A row of flaps, landing left to right `stagger` frames apart. */
const FlapRow = ({
	text,
	n,
	settle,
	stagger = 1,
	frame,
	w,
	h,
	gap,
	seed,
	color,
	spinFrom,
}: {
	text: string;
	n: number;
	settle: number;
	stagger?: number;
	frame: number;
	w: number;
	h: number;
	gap: number;
	seed: number;
	color?: string;
	spinFrom?: number;
}) => (
	<div style={{ display: "flex", gap }}>
		{[...text.slice(0, n).padEnd(n, " ")].map((ch, i) => (
			<Flap key={i} ch={ch} settle={settle + i * stagger} frame={frame} w={w} h={h} seed={seed * 97 + i} color={color} spinFrom={spinFrom} />
		))}
	</div>
);

const PlaneGlyph = ({ size, color }: { size: number; color: string }) => (
	<svg width={size} height={size} viewBox="-50 -50 100 100">
		<Glyph name="plane" size={100} color={color} />
	</svg>
);

// ---- where-to-next -----------------------------------------------------------------------------------

const WhereToNext = (p: BoardProps) => {
	const { frame, px, width, height, reelData, currentTrip, nightsText, stopsText } = p;
	const first = passportStamps(reelData, currentTrip)[0];
	const wheel = wheelAt(frame, wheelTarget(currentTrip, reelData), WN_SPIN, WN_GAPS);
	const small = Math.min(px(48), (width * 0.92) / 20 - px(4));
	const big = Math.min(px(150), (width * 0.92) / wheel.width - px(10), height * 0.16);
	const open = ease(frame, WN_OPEN, Easing.in(Easing.cubic));
	const fade = 1 - ease(frame, [WN_OPEN[1] - 6, WN_OPEN[1]]);
	const blink = Math.floor(frame / 6) % 2 === 0;

	const board = (
		<AbsoluteFill
			style={{
				background: `radial-gradient(ellipse at 50% 40%, #1a1f25, ${BOARD} 70%)`,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: Math.min(px(60), height * 0.05),
			}}
		>
			<div style={{ display: "flex", alignItems: "center", gap: px(18) }}>
				<PlaneGlyph size={px(56)} color={AMBER} />
				<FlapRow text="WHERE TO NEXT?" n={14} settle={WN_HEADER} frame={frame} w={small * 1.25} h={small * 1.8} gap={px(4)} seed={3} color={AMBER} />
			</div>
			{/* The wheel: each place flips in over a frame of blur and holds; the destination lands a letter at a time. */}
			<div style={{ display: "flex", gap: px(10), filter: !wheel.landed && frame - wheel.changedAt < 1 ? `blur(${px(1.5)}px)` : undefined }}>
				{[...wheel.text].map((ch, i) => (
					<Flap
						key={i}
						ch={ch}
						settle={wheel.landed ? wheel.landAt + i : wheel.changedAt + 1}
						frame={frame}
						w={big}
						h={big * 1.45}
						seed={100 + i}
						spinFrom={WN_SPIN - 2}
					/>
				))}
			</div>
			<FlapRow
				text={`${nightsText} · ${stopsText}`.toUpperCase()}
				n={20}
				settle={WN_LINES}
				frame={frame}
				w={small}
				h={small * 1.45}
				gap={px(4)}
				seed={7}
				spinFrom={WN_LINES - 6}
			/>
			<div style={{ display: "flex", alignItems: "center", gap: px(28) }}>
				<FlapRow
					text={`DEPARTS ${first?.when || ""}`}
					n={14}
					settle={WN_LINES + 6}
					frame={frame}
					w={small}
					h={small * 1.45}
					gap={px(4)}
					seed={11}
					spinFrom={WN_LINES}
				/>
				<div
					style={{
						fontFamily: "Poppins",
						fontWeight: 800,
						fontSize: px(40),
						letterSpacing: "0.14em",
						color: GREEN,
						textShadow: `0 0 ${px(14)}px rgba(47,227,154,.8)`,
						opacity: frame >= WN_STATUS ? (blink ? 1 : 0.3) : 0,
					}}
				>
					BOARDING
				</div>
			</div>
		</AbsoluteFill>
	);
	// The board opens as one big flap: its top half folds up and away, its bottom half down.
	return (
		<AbsoluteFill style={{ perspective: px(2200) }}>
			<AbsoluteFill style={{ clipPath: "inset(0 0 50% 0)", transformOrigin: "50% 0%", transform: `rotateX(${open * 100}deg)`, opacity: fade }}>
				{board}
			</AbsoluteFill>
			<AbsoluteFill style={{ clipPath: "inset(50% 0 0 0)", transformOrigin: "50% 100%", transform: `rotateX(${-open * 100}deg)`, opacity: fade }}>
				{board}
			</AbsoluteFill>
		</AbsoluteFill>
	);
};

// ---- departure-board ---------------------------------------------------------------------------------

/** Characters across: date 6, destination 12, via 2, nights 2, the remark 7, and a gap between each. */
const COLUMNS = { when: 0, city: 7, via: 20, nights: 23, remark: 26, width: 33 };
const CITY = 12;

const DepartureBoard = (p: BoardProps) => {
	const { frame, px, width, height, reelData, currentTrip, brandColor } = p;
	const rows = boardRows(passportStamps(reelData, currentTrip), DB_ROWS, CITY);
	const bar = { top: height * 0.075, height: Math.min(px(96), height * 0.09) };
	const tableTop = bar.top + bar.height + px(40);
	// As wide as the frame allows, and as tall: landscape runs out of height before width.
	const rowRoom = (height * 0.94 - tableTop) / (rows.length + 1);
	const gap = px(3);
	// A column is a flap and the gap after it.
	const cw = Math.min(px(40), (width * 0.94) / COLUMNS.width - gap, (rowRoom - px(20)) / 1.45);
	const ch = cw * 1.45;
	const col = (n: number) => n * (cw + gap);
	const rowH = ch + px(24);
	const top = Math.max(tableTop, (height - (rows.length + 1) * rowH) / 2);
	const tip = ease(frame, DB_TIP, Easing.in(Easing.cubic));
	const glow = ease(frame, DB_HIGHLIGHT);
	const heading = {
		position: "absolute" as const,
		fontFamily: "Poppins",
		fontWeight: 700,
		fontSize: Math.max(px(14), cw * 0.45),
		letterSpacing: "0.18em",
		color: AMBER,
	};
	const remarkColor = { BOARDING: GREEN, "DAY STOP": AMBER, "ON TIME": INK };
	return (
		<AbsoluteFill style={{ perspective: px(2400) }}>
			<AbsoluteFill
				style={{
					background: `linear-gradient(${BOARD}, #151a20)`,
					transformOrigin: "50% 100%",
					transform: `translateY(${-tip * height * 0.35}px) rotateX(${tip * 62}deg)`,
					opacity: 1 - ease(frame, [DB_TIP[1] - 8, DB_TIP[1]]),
				}}
			>
				<div
					style={{
						position: "absolute",
						left: 0,
						right: 0,
						top: bar.top,
						height: bar.height,
						background: AMBER,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: `0 ${px(40)}px`,
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: px(16),
							fontFamily: "Poppins",
							fontWeight: 800,
							fontSize: bar.height * 0.54,
							letterSpacing: "0.1em",
							color: BOARD,
						}}
					>
						<PlaneGlyph size={bar.height * 0.58} color={BOARD} />
						DEPARTURES
					</div>
					<div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: bar.height * 0.46, color: BOARD }}>{PASS_DECOR.boarding}</div>
				</div>
				<div style={{ position: "absolute", left: (width - col(COLUMNS.width)) / 2, top }}>
					<div style={{ position: "relative", height: rowH * 0.6 }}>
						<span style={{ ...heading, left: col(COLUMNS.when) }}>DATE</span>
						<span style={{ ...heading, left: col(COLUMNS.city) }}>DESTINATION</span>
						<span style={{ ...heading, left: col(COLUMNS.via) }}>VIA</span>
						<span style={{ ...heading, left: col(COLUMNS.nights) }}>NTS</span>
						<span style={{ ...heading, left: col(COLUMNS.remark) }}>REMARKS</span>
					</div>
					{rows.map((row, r) => {
						const settle = DB_FIRST + r * DB_EVERY;
						const on = (after: number) => frame >= settle + after;
						const blink = r === 0 && frame >= DB_HIGHLIGHT[0] ? Math.floor(frame / 6) % 2 === 0 : true;
						return (
							<div key={r} style={{ position: "relative", height: rowH }}>
								{r === 0 ? (
									<div
										style={{
											position: "absolute",
											left: -px(16),
											top: -px(8),
											height: ch + px(16),
											width: (col(COLUMNS.width) + px(32)) * glow,
											background: "rgba(246,199,68,.16)",
											borderRadius: px(8),
										}}
									/>
								) : null}
								<div style={{ position: "absolute", left: col(COLUMNS.when) }}>
									<FlapRow text={row.when} n={6} settle={settle} frame={frame} w={cw} h={ch} gap={gap} seed={r * 10 + 1} spinFrom={settle - 8} />
								</div>
								<div style={{ position: "absolute", left: col(COLUMNS.city) }}>
									<FlapRow text={row.city} n={CITY} settle={settle + 2} frame={frame} w={cw} h={ch} gap={gap} seed={r * 10 + 2} spinFrom={settle - 8} />
								</div>
								<div
									style={{
										position: "absolute",
										left: col(COLUMNS.via),
										width: col(2) - gap,
										height: ch,
										borderRadius: cw * 0.2,
										background: "#23282d",
										display: "grid",
										placeItems: "center",
										opacity: on(4) ? 1 : 0,
									}}
								>
									{/* How you get there; the first stop has no leg before it and gets the brand's pin. */}
									<svg width={ch * 0.8} height={ch * 0.8} viewBox="-50 -50 100 100">
										<Glyph name={row.via ?? "map-marker"} size={100} color={row.via ? INK : brandColor} />
									</svg>
								</div>
								<div style={{ position: "absolute", left: col(COLUMNS.nights) }}>
									<FlapRow
										text={row.nights.padStart(2, " ")}
										n={2}
										settle={settle + 5}
										frame={frame}
										w={cw}
										h={ch}
										gap={gap}
										seed={r * 10 + 3}
										spinFrom={settle - 8}
									/>
								</div>
								<div
									style={{
										position: "absolute",
										left: col(COLUMNS.remark),
										top: ch * 0.24,
										whiteSpace: "nowrap",
										fontFamily: "Poppins",
										fontWeight: 800,
										fontSize: ch * 0.42,
										letterSpacing: "0.08em",
										color: remarkColor[row.remark],
										textShadow: r === 0 ? `0 0 ${px(12)}px rgba(47,227,154,.7)` : "none",
										opacity: on(6) ? (blink ? 1 : 0.3) : 0,
									}}
								>
									{row.remark}
								</div>
							</div>
						);
					})}
				</div>
			</AbsoluteFill>
		</AbsoluteFill>
	);
};

export const DepartureBoardIntro = ({ style, ...p }: BoardProps & { style: "where-to-next" | "departure-board" }) =>
	style === "where-to-next" ? <WhereToNext {...p} /> : <DepartureBoard {...p} />;
